import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classes from './index.module.css'

export default function EditableNameInput({
  name,
  setName,
  onNameChange,
  onSave,
  isCreate = false,
  initialValue,
  setInitialValue = () => {},
  setInitialFlag = () => {},
  setIsEdit = () => {},
  placeholder = 'Collection name',
  doubleClickToEdit = true,
}) {
  const [toggle, setToggle] = useState(true)
  const [text, setText] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    if (name !== null && !initialValue) {
      setInitialValue(name)
    }
  }, [name, initialValue])

  useEffect(() => {
    if (name === null) {
      setText('')
    } else if (name) {
      setText(name)
    }
  }, [name])

  useEffect(() => {
    isCreate && !text.length && setToggle(false)
  }, [isCreate, text])

  useEffect(() => {
    const handleClickOutside = event => {
      if (!text) {
        setIsEdit(true)
      }
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        if (text.length > 0) {
          // Use onNameChange if provided (for simple usage like songs)
          if (onNameChange) {
            onNameChange(text)
          } else {
            // Use setName for complex usage (collections)
            setName(text)
          }
          if (text !== initialValue) {
            setInitialFlag(true)
            if (onSave) onSave(text)
          } else {
            setInitialFlag(false)
          }
        } else {
          if (!onNameChange) {
            setName(null)
          }
          setIsEdit(false)
        }
        setToggle(text.length > 0)
        setIsEdit(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [text, setName, onNameChange, onSave, initialValue, setInitialFlag, setIsEdit])

  const toggleInput = () => {
    setToggle(false)
    setIsEdit(true)
  }

  const handleClick = () => {
    if (!doubleClickToEdit) {
      toggleInput()
    }
  }

  const handleChange = event => {
    setText(event.target.value)
    setIsEdit(true)
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      if (text.length > 0) {
        // Use onNameChange if provided (for simple usage like songs)
        if (onNameChange) {
          onNameChange(text)
        } else {
          // Use setName for complex usage (collections)
          setName(text)
        }
        setIsEdit(false)
        if (text !== initialValue) {
          setInitialFlag(true)
          if (onSave) onSave(text)
        } else {
          setInitialFlag(false)
        }
      }
      setToggle(text.length > 0)
    } else if (event.key === 'Escape') {
      setText(name || '')
      setToggle(true)
      setIsEdit(false)
    }
  }

  return (
    <div ref={inputRef}>
      {toggle ? (
        <span 
          className={classes.spanName} 
          onClick={handleClick}
          onDoubleClick={doubleClickToEdit ? toggleInput : undefined}
          style={{ cursor: 'text' }}
        >
          {name}
        </span>
      ) : (
        <input
          className={classes.inputName}
          type="text"
          placeholder={placeholder}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      )}
    </div>
  )
}

EditableNameInput.propTypes = {
  name: PropTypes.string,
  setName: PropTypes.func,
  onNameChange: PropTypes.func,
  onSave: PropTypes.func,
  initialValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([null]),
  ]),
  setInitialValue: PropTypes.func,
  isCreate: PropTypes.bool,
  setIsEdit: PropTypes.func,
  setInitialFlag: PropTypes.func,
  placeholder: PropTypes.string,
  doubleClickToEdit: PropTypes.bool,
}
